/**
 * Branding form-effect — injects CSS custom properties, scoped form overrides,
 * Google Fonts, logo, header/footer HTML, and powered-by badge suppression.
 *
 * Targeting strategy:
 *  - CSS rules use `.af-form-renderer` as scope so they apply inside the form
 *    whether it is embedded in a designer preview or deployed standalone.
 *  - Logo/header/footer are prepended to `.af-form-renderer` when found, falling
 *    back to `document.body` for standalone page deployments.
 *  - `!important` is needed to override hardcoded inline styles on form elements.
 *
 * SSR-safe: returns undefined if document is not available.
 */

import type { FormEffectHandler } from '@atlas-forms/types-js';
import { str, track } from './utils';

export const brandingEffect: FormEffectHandler = (config) => {
  if (typeof document === 'undefined') return;

  const created: Element[] = [];

  const primaryColor = str(config.primaryColor);
  const accentColor = str(config.accentColor);
  const backgroundColor = str(config.backgroundColor);
  const fontFamily = str(config.fontFamily);

  // ── CSS: custom props + scoped overrides ──────────────────────────────────
  const cssRules: string[] = [];

  // :root custom properties (useful for downstream app CSS)
  const rootVars: string[] = [];
  if (primaryColor) rootVars.push(`  --af-primary: ${primaryColor};`);
  if (accentColor) rootVars.push(`  --af-accent: ${accentColor};`);
  if (backgroundColor) rootVars.push(`  --af-bg: ${backgroundColor};`);
  if (fontFamily) rootVars.push(`  --af-font-family: ${fontFamily};`);
  if (rootVars.length > 0) {
    cssRules.push(`:root {\n${rootVars.join('\n')}\n}`);
  }

  // Scoped overrides — use !important to beat hardcoded inline styles
  if (primaryColor) {
    cssRules.push(
      `.af-form-renderer button[type="submit"] { background-color: ${primaryColor} !important; border-color: ${primaryColor} !important; }`,
      `.af-form-renderer input:focus, .af-form-renderer select:focus, .af-form-renderer textarea:focus { border-color: ${primaryColor} !important; outline-color: ${primaryColor} !important; }`
    );
  }
  if (backgroundColor) {
    cssRules.push(`.af-form-renderer { background-color: ${backgroundColor} !important; }`);
  }
  if (fontFamily) {
    cssRules.push(`.af-form-renderer { font-family: ${fontFamily} !important; }`);
  }

  if (cssRules.length > 0) {
    const style = document.createElement('style');
    style.setAttribute('data-atlas-branding', 'vars');
    style.textContent = cssRules.join('\n');
    document.head.appendChild(track(style, created));
  }

  // ── Google Fonts ──────────────────────────────────────────────────────────
  if (fontFamily && !fontFamily.includes(';')) {
    const encodedFamily = fontFamily.replace(/\s+/g, '+');
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = `https://fonts.googleapis.com/css2?family=${encodedFamily}:wght@400;600;700&display=swap`;
    document.head.appendChild(track(link, created));
  }

  // ── Logo ──────────────────────────────────────────────────────────────────
  const logoUrl = str(config.logoUrl);
  if (logoUrl) {
    const logoPosition = str(config.logoPosition) || 'left';
    const justifyMap: Record<string, string> = { left: 'flex-start', center: 'center', right: 'flex-end' };
    const justify = justifyMap[logoPosition] ?? 'flex-start';

    const logoWrapper = document.createElement('div');
    logoWrapper.setAttribute('data-atlas-branding', 'logo');
    logoWrapper.style.cssText = `display:flex;justify-content:${justify};padding:8px 16px;`;

    const img = document.createElement('img');
    img.src = logoUrl;
    img.alt = 'Form Logo';
    img.style.cssText = 'max-height:60px;max-width:200px;object-fit:contain;';
    logoWrapper.appendChild(img);

    // Prefer injecting inside the form element (preview / embedded context)
    const formEl = document.querySelector('.af-form-renderer');
    if (formEl) {
      formEl.prepend(track(logoWrapper, created));
    } else {
      document.body.prepend(track(logoWrapper, created));
    }
  }

  // ── Header HTML ───────────────────────────────────────────────────────────
  const headerHtml = str(config.headerHtml);
  if (headerHtml) {
    const headerDiv = document.createElement('div');
    headerDiv.setAttribute('data-atlas-branding', 'header');
    headerDiv.innerHTML = headerHtml;

    const formEl = document.querySelector('.af-form-renderer');
    if (formEl) {
      const logoEl = formEl.querySelector('[data-atlas-branding="logo"]');
      if (logoEl && logoEl.nextSibling) {
        formEl.insertBefore(track(headerDiv, created), logoEl.nextSibling);
      } else if (logoEl) {
        formEl.appendChild(track(headerDiv, created));
      } else {
        formEl.prepend(track(headerDiv, created));
      }
    } else {
      const bodyLogoEl = document.querySelector('[data-atlas-branding="logo"]');
      if (bodyLogoEl && bodyLogoEl.nextSibling) {
        document.body.insertBefore(track(headerDiv, created), bodyLogoEl.nextSibling);
      } else if (bodyLogoEl) {
        document.body.appendChild(track(headerDiv, created));
      } else {
        document.body.prepend(track(headerDiv, created));
      }
    }
  }

  // ── Footer HTML ───────────────────────────────────────────────────────────
  const footerHtml = str(config.footerHtml);
  if (footerHtml) {
    const footerDiv = document.createElement('div');
    footerDiv.setAttribute('data-atlas-branding', 'footer');
    footerDiv.innerHTML = footerHtml;

    const formEl = document.querySelector('.af-form-renderer');
    if (formEl) {
      formEl.appendChild(track(footerDiv, created));
    } else {
      document.body.appendChild(track(footerDiv, created));
    }
  }

  // ── Hide powered-by badge ─────────────────────────────────────────────────
  if (config.hidePoweredByBadge === true) {
    const badgeStyle = document.createElement('style');
    badgeStyle.setAttribute('data-atlas-branding', 'hide-badge');
    badgeStyle.textContent = '.af-powered-by,[data-atlas-powered-by]{display:none!important;}';
    document.head.appendChild(track(badgeStyle, created));
  }

  return () => {
    for (const el of created) {
      el.parentNode?.removeChild(el);
    }
  };
};
